import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import {
  Button,
  FormControl,
  FormGroup,
  ControlLabel,
  Row,
  Col,
  Alert
} from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';

import {
  FileText,
  Home,
  Shield,
  Users,
  Settings,
  Globe,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  Sparkles,
  ChevronRight,
  Zap,
  Check,
  List
} from 'lucide-react';

import s from './ContentManagement.css';
import cs from '../../../components/commonStyle.css';
import messages from '../../../locale/messages';

const LOCALES = [
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'en-US', label: 'English', flag: '🇺🇸' },
  { code: 'fr-FR', label: 'Français', flag: '🇫🇷' },
  { code: 'it-IT', label: 'Italiano', flag: '🇮🇹' },
  { code: 'pt-PT', label: 'Português', flag: '🇵🇹' },
];

// Organized sections based on user's admin pages list
const SECTIONS = {
  static_pages: {
    label: 'Static Pages',
    icon: FileText,
    description: 'About, Privacy, Terms, Help, Cookie Policy, Trust & Safety',
    items: [
      { contentType: 'static_page', contentKey: 'about', label: 'About Us', path: '/siteadmin/edit/staticpage/1' },
      { contentType: 'static_page', contentKey: 'safety', label: 'Trust & Safety', path: '/siteadmin/edit/staticpage/2' },
      { contentType: 'static_page', contentKey: 'privacy', label: 'Privacy Policy', path: '/siteadmin/edit/staticpage/4' },
      { contentType: 'static_page', contentKey: 'help', label: 'Help / FAQ', path: '/siteadmin/edit/staticpage/5' },
      { contentType: 'static_page', contentKey: 'cookies', label: 'Cookie Policy', path: '/siteadmin/edit/staticpage/6' },
      { contentType: 'static_page', contentKey: 'terms-of-service', label: 'Terms of Service', path: '/siteadmin/edit/staticpage/7' },
    ]
  },
  home_blocks: {
    label: 'Home Blocks',
    icon: Home,
    description: 'Banner, Find Your Boat, Map Section, Image Banner, Static Info, Footer',
    items: [
      { contentType: 'banner', contentKey: 'banner_hero', label: 'Banner Caption', path: '/siteadmin/home/caption' },
      { contentType: 'block', contentKey: 'findyourvehicle', label: 'Find Your Boat', path: '/siteadmin/home/find-your-boat', dynamic: true },
      { contentType: 'block', contentKey: 'mapsection', label: 'Map Section', path: '/siteadmin/home/map-section' },
      { contentType: 'banner', contentKey: 'imagebanner_1', label: 'Image Banner', path: '/siteadmin/home/banner' },
      { contentType: 'block', contentKey: 'staticinfo', label: 'Static Info Block', path: '/siteadmin/home/static-info-block', dynamic: true },
      { contentType: 'footer', contentKey: 'footer_col1', label: 'Footer Column 1', path: '/siteadmin/home/footer-block' },
      { contentType: 'footer', contentKey: 'footer_col2', label: 'Footer Column 2', path: '/siteadmin/home/footer-block' },
      { contentType: 'footer', contentKey: 'footer_col3', label: 'Footer Column 3', path: '/siteadmin/home/footer-block' },
    ]
  },
  cancellation_policies: {
    label: 'Cancellation Policies',
    icon: Shield,
    description: 'Flexible, Moderate, Strict policies with all their content sections',
    items: [
      { contentType: 'policy', contentKey: 'cancellation_flexible', label: 'Flexible Policy', path: '/siteadmin/home/cancellation-policies' },
      { contentType: 'policy', contentKey: 'cancellation_moderate', label: 'Moderate Policy', path: '/siteadmin/home/cancellation-policies' },
      { contentType: 'policy', contentKey: 'cancellation_strict', label: 'Strict Policy', path: '/siteadmin/home/cancellation-policies' },
      { contentType: 'policy', contentKey: 'cancellation_shared', label: 'Shared Content', path: '/siteadmin/home/cancellation-policies' },
    ]
  },
  why_host: {
    label: 'Why Host Page',
    icon: Users,
    description: 'Blocks 1-7 and Reviews for the Why Host page',
    items: [
      { contentType: 'block', contentKey: 'whyhost_main_1', label: 'Block 1 - Main', path: '/siteadmin/whyHost/Block1', dynamic: true },
      { contentType: 'block', contentKey: 'whyhost_main_2', label: 'Block 2 - Main', path: '/siteadmin/whyHost/Block2', dynamic: true },
      { contentType: 'block', contentKey: 'whyhost_main_3', label: 'Block 3 - Main', path: '/siteadmin/whyHost/Block3', dynamic: true },
      { contentType: 'block', contentKey: 'whyhost_peaceTitleHeading', label: 'Block 4 - Peace Title', path: '/siteadmin/whyHost/Block4', dynamic: true },
      { contentType: 'block', contentKey: 'whyhost_workTitleHeading', label: 'Block 5 - Work Title', path: '/siteadmin/whyHost/Block5', dynamic: true },
      { contentType: 'block', contentKey: 'review_1', label: 'Review 1', path: '/siteadmin/whyHost/review' },
      { contentType: 'block', contentKey: 'review_2', label: 'Review 2', path: '/siteadmin/whyHost/review' },
      { contentType: 'block', contentKey: 'review_3', label: 'Review 3', path: '/siteadmin/whyHost/review' },
      { contentType: 'block', contentKey: 'whyhost_buttonLabel', label: 'Block 7 - Button', path: '/siteadmin/whyHost/Block7', dynamic: true },
    ]
  },
  list_settings: {
    label: 'List Settings',
    icon: List,
    description: 'Individual items are managed from their respective pages. Use links below to access.',
    items: [
      { contentType: 'link_only', contentKey: 'listsettings_1', label: 'Boat Types', path: '/siteadmin/listsettings/1' },
      { contentType: 'link_only', contentKey: 'listsettings_10', label: 'Features', path: '/siteadmin/listsettings/10' },
      { contentType: 'link_only', contentKey: 'listsettings_13', label: 'Guest Requirements', path: '/siteadmin/listsettings/13' },
      { contentType: 'link_only', contentKey: 'listsettings_14', label: 'Boat Rules', path: '/siteadmin/listsettings/14' },
      { contentType: 'link_only', contentKey: 'listsettings_15', label: 'How Renters Book', path: '/siteadmin/listsettings/15' },
      { contentType: 'link_only', contentKey: 'listsettings_18', label: 'Minimum Days', path: '/siteadmin/listsettings/18' },
      { contentType: 'link_only', contentKey: 'listsettings_19', label: 'Maximum Days', path: '/siteadmin/listsettings/19' },
    ]
  },
};

const GET_ALL_DYNAMIC_CONTENT = gql`
  query getAllDynamicContent {
    getAllDynamicContent {
      id
      contentType
      contentKey
      locale
      title
      content
      metaTitle
      metaDescription
    }
  }
`;

const UPDATE_DYNAMIC_CONTENT = gql`
  mutation updateDynamicContent(
    $contentType: String!
    $contentKey: String!
    $locale: String!
    $title: String
    $content: String
    $metaTitle: String
    $metaDescription: String
  ) {
    updateDynamicContent(
      contentType: $contentType
      contentKey: $contentKey
      locale: $locale
      title: $title
      content: $content
      metaTitle: $metaTitle
      metaDescription: $metaDescription
    ) {
      id
      status
    }
  }
`;

class ContentManagement extends Component {
  static propTypes = {
    title: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      activeSection: 'static_pages',
      activeItem: null,
      activeLocale: 'es',
      editedContent: {},
      saving: false,
      saveStatus: null,
      translating: false,
      translateStatus: null,
      forceTranslate: false,
    };
  }

  componentDidMount() {
    // Set first item as active
    const firstSection = Object.keys(SECTIONS)[0];
    const firstItem = SECTIONS[firstSection]?.items[0];
    if (firstItem) {
      this.setState({ activeSection: firstSection, activeItem: firstItem });
    }
    // Process data if already loaded
    if (this.props.data?.getAllDynamicContent) {
      this.processContentData();
    }
  }

  componentDidUpdate(prevProps) {
    // Process when data first arrives or when refetched
    const prevData = prevProps.data?.getAllDynamicContent;
    const currData = this.props.data?.getAllDynamicContent;
    if ((!prevData && currData) || (prevData !== currData && currData)) {
      this.processContentData();
    }
  }

  processContentData = () => {
    const { data } = this.props;
    if (!data?.getAllDynamicContent) return;

    const editedContent = {};
    data.getAllDynamicContent.forEach(item => {
      const key = `${item.contentType}_${item.contentKey}_${item.locale}`;
      editedContent[key] = {
        title: item.title || '',
        content: item.content || '',
        metaTitle: item.metaTitle || '',
        metaDescription: item.metaDescription || '',
      };
    });
    this.setState({ editedContent });
  };

  getContentKey = (contentType, contentKey, locale) => `${contentType}_${contentKey}_${locale}`;

  getCurrentContent = () => {
    const { activeItem, activeLocale, editedContent } = this.state;
    if (!activeItem) return { title: '', content: '', metaTitle: '', metaDescription: '' };

    const key = this.getContentKey(activeItem.contentType, activeItem.contentKey, activeLocale);
    return editedContent[key] || { title: '', content: '', metaTitle: '', metaDescription: '' };
  };

  handleContentChange = (field, value) => {
    const { activeItem, activeLocale, editedContent } = this.state;
    if (!activeItem) return;

    const key = this.getContentKey(activeItem.contentType, activeItem.contentKey, activeLocale);
    const currentContent = this.getCurrentContent();

    this.setState({
      editedContent: {
        ...editedContent,
        [key]: { ...currentContent, [field]: value },
      },
      saveStatus: null,
    });
  };

  handleSave = async () => {
    const { activeItem, activeLocale } = this.state;
    const { mutate } = this.props;
    if (!activeItem) return;

    const content = this.getCurrentContent();
    this.setState({ saving: true, saveStatus: null });

    try {
      const result = await mutate({
        variables: {
          contentType: activeItem.contentType,
          contentKey: activeItem.contentKey,
          locale: activeLocale,
          title: content.title,
          content: content.content,
          metaTitle: content.metaTitle,
          metaDescription: content.metaDescription,
        },
      });

      if (result.data?.updateDynamicContent?.status === 'success') {
        this.setState({ saving: false, saveStatus: 'success' });
        this.props.data.refetch();
      } else {
        this.setState({ saving: false, saveStatus: 'error' });
      }
    } catch (error) {
      console.error('Save error:', error);
      this.setState({ saving: false, saveStatus: 'error' });
    }
  };

  handleTranslateWithAI = async () => {
    const { activeItem, forceTranslate, editedContent } = this.state;
    if (!activeItem) return;

    const esKey = this.getContentKey(activeItem.contentType, activeItem.contentKey, 'es');
    const esContent = editedContent[esKey];

    if (!esContent?.content && !esContent?.title) {
      this.setState({ translateStatus: { status: 'error', message: 'No Spanish content to translate' } });
      return;
    }

    this.setState({ translating: true, translateStatus: null });

    const targetLocales = LOCALES.filter(l => l.code !== 'es');
    let translated = 0;
    let errors = 0;

    for (const locale of targetLocales) {
      const targetKey = this.getContentKey(activeItem.contentType, activeItem.contentKey, locale.code);
      const existing = editedContent[targetKey];

      if (!forceTranslate && existing?.content) continue;

      try {
        const textToTranslate = esContent.content || esContent.title;
        const response = await fetch('/graphql', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            query: `mutation translateSingleContent($text: String!, $targetLocale: String!) {
              translateSingleContent(text: $text, targetLocale: $targetLocale) {
                status
                translatedText
              }
            }`,
            variables: { text: textToTranslate, targetLocale: locale.code }
          })
        });

        const result = await response.json();

        if (result.data?.translateSingleContent?.status === 'success') {
          const translatedText = result.data.translateSingleContent.translatedText;
          this.setState(prev => ({
            editedContent: {
              ...prev.editedContent,
              [targetKey]: {
                ...(prev.editedContent[targetKey] || {}),
                title: esContent.title ? translatedText : (prev.editedContent[targetKey]?.title || ''),
                content: esContent.content ? translatedText : (prev.editedContent[targetKey]?.content || ''),
              }
            }
          }));
          translated++;
        } else {
          errors++;
        }
      } catch (error) {
        console.error('Translation error:', error);
        errors++;
      }
    }

    this.setState({
      translating: false,
      translateStatus: {
        status: errors === 0 ? 'success' : 'partial',
        message: `Translated: ${translated} languages${errors > 0 ? `, Errors: ${errors}` : ''}`
      }
    });
  };

  render() {
    const {
      activeSection,
      activeItem,
      activeLocale,
      saving,
      saveStatus,
      translating,
      translateStatus,
      forceTranslate
    } = this.state;
    const { data } = this.props;
    const content = this.getCurrentContent();
    const section = SECTIONS[activeSection];
    const isStaticPage = activeItem?.contentType === 'static_page';

    return (
      <div className={cx(s.pagecontentWrapper, cs.commonAdminBorderSection)}>
        {/* Header */}
        <div className={s.header}>
          <h1 className={cs.commonTotalText}>
            <Globe size={28} />
            Content Management (i18n)
          </h1>
          <p className={s.subtitle}>
            Manage all translatable content. Select section → item → locale to edit.
          </p>
        </div>

        {/* Section Tabs */}
        <div className={s.sectionTabs}>
          {Object.entries(SECTIONS).map(([key, sec]) => {
            const Icon = sec.icon;
            return (
              <button
                key={key}
                className={cx(s.sectionTab, { [s.active]: activeSection === key })}
                onClick={() => {
                  const firstItem = sec.items[0];
                  this.setState({ activeSection: key, activeItem: firstItem, saveStatus: null, translateStatus: null });
                }}
              >
                <Icon size={18} />
                <span>{sec.label}</span>
                <span className={s.badge}>{sec.items.length}</span>
              </button>
            );
          })}
        </div>

        <div className={s.mainLayout}>
          {/* Items List */}
          <div className={s.itemsList}>
            <div className={s.listHeader}>
              <h4>{section?.label}</h4>
              <span className={s.listCount}>{section?.items.length} items</span>
            </div>
            <p className={s.listDesc}>{section?.description}</p>
            <div className={s.listItems}>
              {section?.items.map((item, idx) => (
                <button
                  key={idx}
                  className={cx(s.listItem, { [s.active]: activeItem?.contentKey === item.contentKey })}
                  onClick={() => this.setState({ activeItem: item, saveStatus: null, translateStatus: null })}
                >
                  <span>{item.label}</span>
                  <ChevronRight size={16} />
                </button>
              ))}
            </div>
          </div>

          {/* Editor Panel */}
          <div className={s.editorPanel}>
            {data?.loading ? (
              <div className={s.loading}>
                <Loader2 size={32} className={s.spinner} />
                <span>Loading...</span>
              </div>
            ) : !activeItem ? (
              <div className={s.emptyState}>
                <FileText size={48} />
                <p>Select an item to edit</p>
              </div>
            ) : activeItem.contentType === 'link_only' ? (
              /* Link Only - No editor, just redirect link */
              <div className={s.linkOnlyPanel}>
                <div className={s.editorHeader}>
                  <h3>{activeItem.label}</h3>
                </div>
                <div className={s.linkOnlyContent}>
                  <Settings size={48} />
                  <p>This section contains multiple items that are managed individually.</p>
                  <p>Each item has its own translation fields on the original page.</p>
                  <a href={activeItem.path} target="_blank" rel="noopener noreferrer" className={s.linkOnlyBtn}>
                    <ChevronRight size={18} />
                    Go to {activeItem.label} Management
                  </a>
                </div>
              </div>
            ) : (
              <>
                {/* Editor Header */}
                <div className={s.editorHeader}>
                  <h3>{activeItem.label}</h3>
                  <a href={activeItem.path} target="_blank" rel="noopener noreferrer" className={s.viewLink}>
                    View original page →
                  </a>
                </div>

                {/* Locale Tabs */}
                <div className={s.localeTabs}>
                  {LOCALES.map(locale => (
                    <button
                      key={locale.code}
                      className={cx(s.localeTab, { [s.active]: activeLocale === locale.code })}
                      onClick={() => this.setState({ activeLocale: locale.code, saveStatus: null })}
                    >
                      <span className={s.flag}>{locale.flag}</span>
                      <span>{locale.label}</span>
                    </button>
                  ))}
                </div>

                {/* AI Translation */}
                <div className={s.aiSection}>
                  <div className={s.aiHeader}>
                    <Sparkles size={20} />
                    <span>AI Translation (OpenAI)</span>
                  </div>
                  <div className={s.aiControls}>
                    <label className={s.checkbox}>
                      <input
                        type="checkbox"
                        checked={forceTranslate}
                        onChange={(e) => this.setState({ forceTranslate: e.target.checked })}
                      />
                      Force All (overwrite existing)
                    </label>
                    <Button
                      bsStyle="warning"
                      className={s.translateBtn}
                      onClick={this.handleTranslateWithAI}
                      disabled={translating}
                    >
                      {translating ? (
                        <><Loader2 size={16} className={s.spinner} /> Translating...</>
                      ) : (
                        <><Zap size={16} /> Translate to All Languages</>
                      )}
                    </Button>
                  </div>
                  {translateStatus && (
                    <div className={cx(s.statusMsg, s[translateStatus.status])}>
                      {translateStatus.status === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
                      <span>{translateStatus.message}</span>
                    </div>
                  )}
                </div>

                {/* Form Fields */}
                <div className={s.formFields}>
                  <FormGroup>
                    <ControlLabel>Title</ControlLabel>
                    <FormControl
                      type="text"
                      value={content.title}
                      onChange={(e) => this.handleContentChange('title', e.target.value)}
                      placeholder="Enter title..."
                    />
                  </FormGroup>

                  <FormGroup>
                    <ControlLabel>Content</ControlLabel>
                    <FormControl
                      componentClass="textarea"
                      rows={8}
                      value={content.content}
                      onChange={(e) => this.handleContentChange('content', e.target.value)}
                      placeholder="Enter content (HTML supported)..."
                      className={s.textarea}
                    />
                  </FormGroup>

                  {isStaticPage && (
                    <>
                      <FormGroup>
                        <ControlLabel>Meta Title (SEO)</ControlLabel>
                        <FormControl
                          type="text"
                          value={content.metaTitle}
                          onChange={(e) => this.handleContentChange('metaTitle', e.target.value)}
                          placeholder="Meta title..."
                        />
                      </FormGroup>
                      <FormGroup>
                        <ControlLabel>Meta Description (SEO)</ControlLabel>
                        <FormControl
                          componentClass="textarea"
                          rows={2}
                          value={content.metaDescription}
                          onChange={(e) => this.handleContentChange('metaDescription', e.target.value)}
                          placeholder="Meta description..."
                        />
                      </FormGroup>
                    </>
                  )}
                </div>

                {/* Status & Actions */}
                {saveStatus === 'success' && (
                  <Alert bsStyle="success" className={s.alert}>
                    <CheckCircle size={18} /> Content saved successfully!
                  </Alert>
                )}
                {saveStatus === 'error' && (
                  <Alert bsStyle="danger" className={s.alert}>
                    <AlertCircle size={18} /> Error saving. Please try again.
                  </Alert>
                )}

                <div className={s.actions}>
                  <Button bsStyle="primary" onClick={this.handleSave} disabled={saving} className={s.saveBtn}>
                    {saving ? <Loader2 size={16} className={s.spinner} /> : <Save size={16} />}
                    Save Current Locale
                  </Button>
                  <Button bsStyle="default" onClick={() => this.props.data.refetch()} disabled={saving}>
                    <RefreshCw size={16} /> Refresh
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default compose(
  injectIntl,
  withStyles(s, cs),
  graphql(GET_ALL_DYNAMIC_CONTENT),
  graphql(UPDATE_DYNAMIC_CONTENT),
)(ContentManagement);
